import { useId, useState } from 'react'
import { PersonName } from './PersonName'
import { api } from '@/api/client'
import { fmtDate } from '@/lib/format'
import { needsBuilderReply } from '@/lib/stages'
import type { TaskDetail, Role } from '@/lib/types'
import { MessageComposer, MessageSendButton } from './MessageComposer'
import { Picker } from './Picker'
import { BuilderAttention } from './BuilderAttention'
import './authoring-review.css'

export function AuthoringReview({ data, readOnly = false }: { data: Pick<TaskDetail, 'task' | 'reviews' | 'people'>; role: Role; readOnly?: boolean }) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [draft, setDraft] = useState<{ id: string; text: string } | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const controlId = useId()
  const reviews = (data.reviews ?? []).filter(r => r.kind === 'authoring_blocker')
    .sort((a, b) => b.requested_at.localeCompare(a.requested_at))
  const current = !readOnly && data.task.lifecycle !== 'retired' && needsBuilderReply(data.task)
    ? reviews.find(r => r.review_id === data.task.authoring_review_id) : undefined
  const selected = reviews.find(r => r.review_id === selectedId) ?? current ?? reviews[0]
  const isCurrent = !!current && selected === current
  const reply = draft?.id === current?.review_id ? draft?.text ?? '' : current?.response ?? ''
  const canSave = !!current && !!reply.trim() && reply.trim() !== (current.response ?? '').trim() && !busy
  if (!selected || (!readOnly && !current)) return null

  async function save() {
    if (!current || !canSave) return
    setBusy(true); setError('')
    try {
      await api.post(`/tasks/${data.task.task_id}/authoring-reviews/${current.review_id}/reply`, {
        revision: data.task.revision ?? 0, notes: reply,
      })
      window.location.reload()
    } catch (e) { setError(String(e)) } finally { setBusy(false) }
  }

  return <section className={`authoring-review${isCurrent ? ' authoring-review-current' : ''}`} aria-label="Task authoring review">
    <header className="authoring-review-heading">
      <div className="authoring-review-title">
        <h3>Task authoring review</h3>
        {isCurrent && (current?.response
          ? <span className={`chip ${reply.trim() === current.response.trim() ? 'ok' : 'warn'}`}>
            {reply.trim() === current.response.trim() ? 'Reply saved' : 'Unsaved reply'}
          </span>
          : <BuilderAttention />)}
      </div>
      <div className="authoring-picker">
        <Picker id={`${controlId}-question`} label="View question" value={selected.review_id} disabled={busy}
          onChange={value => { setSelectedId(value === current?.review_id ? null : value); setError('') }}
          options={[...(current ? [current] : []), ...reviews.filter(review => review !== current)].map(review => ({
            value: review.review_id,
            label: review === current ? 'Current question' : 'Previous question',
            detail: fmtDate(review.requested_at),
          }))} />
      </div>
    </header>
    <section className="authoring-question" aria-label={isCurrent ? 'Current question' : 'Previous question'}>
      <p className="authoring-message-meta">Authoring agent · <time dateTime={selected.requested_at}>{fmtDate(selected.requested_at)}</time></p>
      <p className="authoring-question-text prewrap">{selected.notes}</p>
    </section>
    {isCurrent && current ? <div className="authoring-reply-form">
      <label htmlFor={`${controlId}-reply`}>Reply to this question</label>
      <MessageComposer compact onSend={() => void save()} sendDisabled={!canSave}
        actions={<MessageSendButton type="button" label="Save reply" busy={busy} disabled={!canSave} onClick={() => void save()} />}>
        <textarea id={`${controlId}-reply`} placeholder="Write your answer…" rows={2}
          value={reply} disabled={busy} maxLength={1600}
          onChange={event => setDraft({ id: current.review_id, text: event.target.value })} />
      </MessageComposer>
      {current.response && <p className="authoring-message-meta">
        Last saved{current.returned_at && <> · <time dateTime={current.returned_at}>{fmtDate(current.returned_at)}</time></>}
        {(current.returned_by || current.returned_by_id) && <> · <PersonName people={data.people} id={current.returned_by_id} email={current.returned_by} /></>}
      </p>}
    </div> : <section className="authoring-saved-reply" aria-label="Saved reply">
      <h4>Saved reply</h4>
      {selected.response ? <>
        <p className="authoring-message-meta">
          {(selected.returned_by || selected.returned_by_id) && <>From <PersonName people={data.people} id={selected.returned_by_id} email={selected.returned_by} /></>}
          {selected.returned_at && <> · <time dateTime={selected.returned_at}>{fmtDate(selected.returned_at)}</time></>}
        </p>
        <p className="prewrap">{selected.response}</p>
      </> : <p className="muted">No reply was saved for this question.</p>}
    </section>}
    {error && <p role="alert">{error}</p>}
  </section>
}
